import React, {FC} from 'react';

import {Colors, Dropdown, Input, Text, Tooltip} from '@optimaxdev/design-system';
import {Icon, Question} from '@optimaxdev/icons';

import {useGetRegions} from 'hooks/location/location';

import {VisiblyDetailsType} from 'types/successPage';

import s from './visiblyExam.scss';

export type PropsType = {
    setVisiblyFields: (data: VisiblyDetailsType) => void;
    visiblyData: VisiblyDetailsType;
};

/**
 * Visibility exam component
 */
export const VisiblyExam: FC<PropsType> = ({setVisiblyFields, visiblyData}) => {
    const regions = useGetRegions('US')?.regions?.US;
    const {errors, values} = visiblyData;

    const handleChange = (key: string, value: string) => {
        setVisiblyFields({
            errors: {...errors, [key]: ''},
            values: {...values, [key]: value},
        });
    };

    const validateDate = (value: string) => {
        const regex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[1-2][0-9]|3[0-1])$/;

        if (!value || !regex.test(value)) {
            setVisiblyFields({
                errors: {
                    ...errors,
                    dateOfBirth: 'Please enter the date in the valid format (YYYY-MM-DD).',
                },
                values,
            });
        }
    };

    const handleDateFormat = (key: string, value: string) => {
        const input = value.replace(/\D/g, '');
        const year = input.slice(0, 4);
        const month = input.slice(4, 6);
        const day = input.slice(6, 8);
        handleChange(
            key,
            `${year}${month.length ? `-${month}` : ''}${day.length ? `-${day}` : ''}`,
        );
    };

    return (
        <>
            <Text
                as="p"
                color={Colors.limedSpruce}
                lineHeight="22px"
                margin="0 0 10px 0"
                size="16px"
                bold
            >
                Quick eligibility check
            </Text>
            <Text as="p" color={Colors.limedSpruce} lineHeight="24px" size="16px">
                Enter your details and continue to see if you’re eligible for a prescription renewal
                test.
            </Text>
            <div className={s.title}>
                <Text color={Colors.limedSpruce} lineHeight="22px" size="16px" bold>
                    Enter your date of birth
                </Text>
                <Tooltip
                    content={
                        <div className={s.tooltip}>
                            <Text color={Colors.limedSpruce} lineHeight="normal" size="14px">
                                Per the guidance of our medical advisors,&nbsp;
                            </Text>
                            <Text color={Colors.limedSpruce} lineHeight="normal" size="14px" bold>
                                our service is not available to patients younger than 18 or older
                                than 55.
                            </Text>
                        </div>
                    }
                >
                    <div className={s.tooltipContainer}>
                        <Icon color={Colors.nevada} height={18} icon={Question} width={18} />
                    </div>
                </Tooltip>
            </div>
            <Input
                isError={Boolean(errors.dateOfBirth)}
                label="YYYY-MM-DD"
                message={errors.dateOfBirth}
                name="dateOfBirth"
                onBlur={e => validateDate(e.currentTarget.value)}
                onChange={e => handleDateFormat('dateOfBirth', e.currentTarget.value)}
                value={values.dateOfBirth}
            />
            <div className={s.title}>
                <Text color={Colors.limedSpruce} lineHeight="22px" size="16px" bold>
                    Select your state
                </Text>
                <Tooltip
                    content={
                        <div className={s.tooltip}>
                            <Text color={Colors.limedSpruce} lineHeight="normal" size="14px">
                                Due to regulations,&nbsp;
                            </Text>
                            <Text color={Colors.limedSpruce} lineHeight="normal" size="14px" bold>
                                some locations are excluded from our vision test experience.&nbsp;
                            </Text>
                            <Text color={Colors.limedSpruce} lineHeight="normal" size="14px">
                                We are currently only servicing US based patients.
                            </Text>
                        </div>
                    }
                    containerClassName={s.tooltipContainer}
                >
                    <div className={s.tooltipContainer}>
                        <Icon color={Colors.nevada} height={18} icon={Question} width={18} />
                    </div>
                </Tooltip>
            </div>
            <Dropdown
                classes={{button: s.dropdown}}
                label="Select State"
                onClick={e => handleChange('state', e?.toString() || '')}
                options={regions}
                selectedValue={values.state}
            />
        </>
    );
};
